import {SelectionModel} from '@angular/cdk/collections';
import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {OrganizationTo} from '../../shared/models/organizationTo';
import {OrganizationsService} from '../../organizations/organizations.service';
import {NavigationService} from '../../navigation/navigation.service';
import {CreateOrganizationComponent} from '../create-organization/create-organization.component';
import {UpdateOrganizationComponent} from '../update-organization/update-organization.component';
import {OrgEntitlementsComponent} from '../org-entitlements/org-entitlements.component';
import {OrganizationSettingsComponent} from '../organization-settings/organization-settings.component';
import {OrgDefaultUserSettingsComponent} from '../org-default-user-settings/org-default-user-settings.component';
import {MapConfigurationComponent} from '../../map-configuration/map-configuration.component';
import {OrgEntitlementsEnum} from '../../shared/enums/org-entitlements.enum';
import { ListPositionAliasComponent } from 'src/app/position-alias/list-position-alias/list-position-alias.component';
import {HomeService} from '../../home/home.service';
import {Roles} from '../../shared/enums/roles';
import {SharedService} from '../../shared/shared.service';
import {BehaviorSubject, merge, Observable, startWith, Subscription} from 'rxjs';
import {FormControl, UntypedFormBuilder, UntypedFormGroup} from "@angular/forms";
import Swal from 'sweetalert2/dist/sweetalert2.js';
import {Alerts} from 'src/app/shared/enums/alerts';
import {catchError, debounceTime, map, switchMap} from "rxjs/operators";

@Component({
    selector: 'app-list-organizations',
    templateUrl: './list-organizations.component.html',
    styleUrls: ['./list-organizations.component.scss']
})
export class ListOrganizationsComponent implements OnInit {

    dialogRef;
    roles = Roles;
    readonly orgEntitlements = OrgEntitlementsEnum;
    organizationList: OrganizationTo[] = [];
    displayedColumns: string[] = ['name', 'clientId', 'psapGroup', 'options'];
    dataSource = new MatTableDataSource<OrganizationTo>(this.organizationList);
    filterCheckboxes: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    selection = new SelectionModel<OrganizationTo>(true, []);
    isLoading = true;
    isDeletingOrg = false;
    displayFailureMsg = false;
    defaultValues = ['0','1','2'];
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    paginatorLength: number;
    totalData : number;
    isRateLimitReached = false;
    sortDirection = "asc";
    searchKeywordFilter = new FormControl();
    searchKeywordValue : string;

    private orgServiceSubscription: Subscription;

    OrgTypes: Array<any> = ['INTERNAL' ,'PROVIDER','PSAP'];

    form: UntypedFormGroup;

    constructor(public dialog: MatDialog, public navigation: NavigationService,
                public orgService: OrganizationsService, public homeService: HomeService,
                public sharedService: SharedService, private cdref: ChangeDetectorRef,
                private fb: UntypedFormBuilder) {
        this.orgServiceSubscription = this.orgService.updateOrganizationList$.subscribe(
            data => {
                this.loadOrganizations();
            });
        this.form = this.fb.group({
            checkArray: this.fb.array([])
        })
    }

    ngOnInit() {
        this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.sort.disableClear = true;
        this.sort.active = "name"
        this.paginator.pageSize = 10;
        this.searchKeywordValue = '';
        this.loadOrganizations();
    }

    setupOrganizations() {
        merge(this.searchKeywordFilter.valueChanges, this.sort.sortChange, this.paginator.page)
            .pipe(
                debounceTime(500),
                startWith({}),
                switchMap(() => {
                    this.isLoading = true;
                    if (this.searchKeywordFilter.value !== this.searchKeywordValue) {
                        this.paginator.pageIndex = 0;
                        this.searchKeywordValue = this.searchKeywordFilter.value;
                    }
                    var filterValue = this.searchKeywordFilter.value == null ? '' : this.searchKeywordFilter.value;
                    return this.getOrganizations(
                        filterValue,
                        this.sort.active,
                        this.sort.direction,
                        this.filterCheckboxes.getValue(),
                        this.paginator.pageIndex,
                        this.paginator.pageSize
                    ).pipe(catchError(() => new Observable(null)));
                }),
                map((data) => {
                    // Flip flag to show that loading has finished.
                    this.isLoading = false;
                    this.isRateLimitReached = data === null;

                    if (data === null) {
                        return [];
                    }

                    /**
                     * We check for the presence of 'data' and 'content' properties to determine how to parse the response.
                     * If 'data' is present, we assume the actual content is within 'data.content'. If 'content' is directly available, we use it. Otherwise, we treat the entire response as the content.
                     * We also handle the total count of organizations, which may be found in 'totalElements', 'totalCount', or within a 'page' object, depending on the API response structure.
                     * This approach ensures that our component can work with various API response formats without breaking, providing flexibility in how the backend can structure its responses.
                     */
                    const payload = data?.data ?? data;
                    const content = payload?.content ?? [];
                    this.totalData = Number(payload?.totalElements ?? payload?.totalCount ?? payload?.page?.totalElements) 
                    ?? content.length;
                    return content;
                })
            )
            .subscribe((data) => {
                this.organizationList = data;
                this.dataSource = new MatTableDataSource<OrganizationTo>(this.organizationList);
                this.setOrganizationList();
                this.loadCustomMapsDisabledState(this.organizationList);
            });
    }

    ngOnDestroy() {
        this.orgServiceSubscription.unsubscribe();
    }

    loadOrganizations() {
        if (this.homeService.userDetails.roles.includes(this.roles.SUPER)) {
             this.setupOrganizations();
        } else {
            this.organizationList = [this.homeService.userDetails.org];
            this.dataSource = new MatTableDataSource<OrganizationTo>(this.organizationList);
            this.isLoading = false;
            this.setOrganizationList();
            this.loadCustomMapsDisabledState(this.organizationList);
        }
    }

    // The /organizations list endpoint doesn't return disabledEntitlements per row (only the
    // single-org GET used by the Entitlements dialog does), so for any row that has CUSTOM_MAPS
    // granted, fetch that org individually to find out whether it's also been toggled off.
    // Bounded to orgs with the entitlement granted, not every row in the list.
    loadCustomMapsDisabledState(orgs: OrganizationTo[]) {
        orgs
            .filter(org => org.entitlements?.includes(this.orgEntitlements.CUSTOM_MAPS))
            .forEach(org => {
                this.orgService.getOrg(org.uuid).subscribe({
                    next: data => {
                        org.disabledEntitlements = data?.disabledEntitlements ?? [];
                    },
                    error: () => {
                        // Leave disabledEntitlements as-is on failure — the icon simply won't
                        // reflect a disabled state until the next successful load.
                    }
                });
            });
    }

    getOrganizations(filterValue, sortActive, sortDirection, orgTypes, pageIndex, pageSize) : Observable<any> {
        if (sortDirection != "" && this.sortDirection != sortDirection) {
            this.sortDirection = sortDirection;
        }
        return this.orgService.getOrganizations(filterValue, sortActive, this.sortDirection, orgTypes, pageIndex, pageSize);
    }

    setOrganizationList() {
        if (this.totalData > 20) {
            this.paginatorLength = this.totalData;
        } else {
            this.paginatorLength = 20;
        }
        this.dataSource.filter = this.dataSource.filter;
        this.dataSource.filterPredicate = this.dataSource.filterPredicate = (orgTo: OrganizationTo, filter: any) => {
            let result = false;
            if (filter.length > 0) {
                let filters = filter.split(',');
                if (!filters.includes(orgTo.type)) {
                    result = true;
                    filters.map(f => {
                        if (!this.OrgTypes.includes(f)) {
                            if (orgTo.name.trim().toLowerCase().indexOf(f) !== -1 ||
                                orgTo.clientId.trim().toLowerCase().indexOf(f) !== -1) {
                                result = true;
                            } else {
                                result = false;
                            }
                        }
                    })
                }
            }

            return result;
        }

        this.dataSource.sortingDataAccessor = (data: OrganizationTo, sortHeaderId: string): string => {
            if (typeof data[sortHeaderId] === 'string') {
                return data[sortHeaderId].toLocaleLowerCase();
            }
            return data[sortHeaderId];
        };
        this.displayFailureMsg = false;
    }

    createOrganization() {
        this.dialogRef = this.dialog.open(CreateOrganizationComponent);
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadOrganizations();
            }
        });
    }

    editOrganization(object) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            org: object,
            organizationList: this.organizationList
        }
        this.dialogRef = this.dialog.open(UpdateOrganizationComponent, {
            data: object,
            width: '550px'
        });

        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadOrganizations();
            }
        }, error => {
            this.dialogRef.close(false);
            this.sharedService.showConfirmAlert(Alerts.ERROR, 'User does not have access to the preferences for organization');
        });
    }

    deleteOrganization(object:any) {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Delete the Organization "' + object.name + '"',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'No, cancel',
            showLoaderOnConfirm: true
        }).then((result:any) => {
            if (result.value) {
                this.isDeletingOrg = true;
                this.orgService.deleteOrganisation(object.uuid).subscribe({
                    next: data => {
                        this.isDeletingOrg = false;
                        this.sharedService.showAlert('success', 'Organization ' + object.name + ' has been deleted.');
                        this.orgService.updateOrganizationList.next();
                    },
                    error: error => {
                        this.isDeletingOrg = false;
                        if (error.error) {
                            this.sharedService.showAlert('error', error.error['Organization Name']);
                        } else {
                            this.sharedService.showAlert('error', 'Organization ' + object.name + ' has not been deleted.');
                        }
                        this.orgService.updateOrganizationList.next();
                    },
                    complete: () => {}
                });
            }
        });
    }

    editOrgEntitlements(object) {
        let finalObject;
        this.orgService.getAllEntitlementDef().subscribe({
            next: data => {
                finalObject = {
                    org: object,
                    entitlements: data
                }

                this.dialogRef = this.dialog.open(OrgEntitlementsComponent, {
                    data: finalObject
                });
                this.dialogRef.afterClosed().subscribe(result => {
                    if (result) {
                        this.loadOrganizations();
                    }
                });
            },
            error: error => {
                this.sharedService.handleErrors(error);
            }
        });
    }

    editSettings(object) {
        this.dialogRef = this.dialog.open(OrganizationSettingsComponent, {
            data: object,
            width: '850px'
        });
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadOrganizations();
            }
        });
    }

    openDefaultUserSettings(object) {
        this.dialogRef = this.dialog.open(OrgDefaultUserSettingsComponent, {
            data: object,
            width: '850px'
        });
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadOrganizations();
            }
        });
    }

    isCustomMapsEnabled(org: OrganizationTo): boolean {
        return org.entitlements?.includes(this.orgEntitlements.CUSTOM_MAPS)
            && !org.disabledEntitlements?.includes(this.orgEntitlements.CUSTOM_MAPS);
    }

    openMapConfiguration(object) {
        this.dialogRef = this.dialog.open(MapConfigurationComponent, {
            data: object,
            width: '75vw',
            maxWidth: '75vw'
        });
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadOrganizations();
            }
        });
    }

    openPositioAliasList(org) {
        this.dialogRef = this.dialog.open(ListPositionAliasComponent, {
            data: org,
            width: '700px'
        });
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadOrganizations();
            }
        });
    }

    navigateToCoS(org: OrganizationTo) {
        this.homeService.navigate.next('listCos');
        this.sharedService.setCurrentOrgId({'orgId': org.clientId, 'orgName': org.name, 'navigate': 'listCos'});
    }

    syncPrimaryPaginator(event: PageEvent) {
        this.paginator.pageIndex = event.pageIndex;
        this.paginator.pageSize = event.pageSize;
        this.paginator.page.emit(event);
    }

    onValChange(e) {
        const selection = parseInt(e.value, 10);
        if (this.filterCheckboxes.value.some((a: any) => a === this.OrgTypes[selection])) {
            this.filterCheckboxes.next(this.filterCheckboxes.value.filter((a: any) => a !== this.OrgTypes[selection]));
        } else {
            if (e.source._checked === false) {
                this.filterCheckboxes.next(this.filterCheckboxes.value.concat(this.OrgTypes[selection]));
            }
        }

        this.setupOrganizations();
    }
}
